/* Ya'axche theme — interaction layer
   - Sticky header on scroll
   - Mobile menu toggle
   - Cart drawer (open/close, fetch contents, AJAX add/update/remove, free-ship progress)
   - Product page: size/qty selectors, variant price update, sticky ATC
   - Quantity steppers (generic)
   - FAQ details are native <details>
   No frameworks. ES2019+. */

(function () {
  "use strict";

  const $ = (sel, root = document) => root.querySelector(sel);
  const $$ = (sel, root = document) => Array.from(root.querySelectorAll(sel));
  const money = (cents) => {
    const v = (cents || 0) / 100;
    try {
      return new Intl.NumberFormat(window.Shopify?.locale || "es-MX", {
        style: "currency",
        currency: window.YA?.currency || "MXN",
        maximumFractionDigits: 0,
      }).format(v);
    } catch (_) {
      return "$" + Math.round(v).toLocaleString("es-MX") + " " + (window.YA?.currency || "MXN");
    }
  };

  /* ---------- HEADER SCROLL ---------- */
  function initHeaderScroll() {
    const header = $(".header-main");
    if (!header) return;
    const onScroll = () => header.classList.toggle("is-scrolled", window.scrollY > 24);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
  }

  /* ---------- MOBILE MENU ---------- */
  function initMobileMenu() {
    const toggle = $("[data-menu-toggle]");
    const nav = $("[data-mobile-nav]");
    if (!toggle || !nav) return;
    toggle.addEventListener("click", () => {
      const open = nav.classList.toggle("is-open");
      toggle.setAttribute("aria-expanded", String(open));
    });
    $$("a", nav).forEach((a) =>
      a.addEventListener("click", () => {
        nav.classList.remove("is-open");
        toggle.setAttribute("aria-expanded", "false");
      })
    );
  }

  /* ---------- CART DRAWER ---------- */
  const Cart = {
    el: null,
    backdrop: null,
    bodyEl: null,
    footEl: null,
    shipEl: null,
    countEls: [],
    free: 0,

    init() {
      this.el = $("[data-cart-drawer]");
      this.backdrop = $("[data-cart-backdrop]");
      this.bodyEl = $("[data-cart-body]");
      this.footEl = $("[data-cart-foot]");
      this.shipEl = $("[data-cart-ship]");
      this.countEls = $$("[data-cart-count]");
      this.free = parseInt(window.YA?.freeShippingThreshold || 80000, 10);
      if (!this.el) return;

      $$("[data-cart-open]").forEach((b) => b.addEventListener("click", (e) => { e.preventDefault(); this.open(); }));
      $$("[data-cart-close]").forEach((b) => b.addEventListener("click", () => this.close()));
      if (this.backdrop) this.backdrop.addEventListener("click", () => this.close());

      document.addEventListener("keydown", (e) => { if (e.key === "Escape") this.close(); });

      // AJAX add-to-cart forms
      document.addEventListener("submit", (e) => {
        const form = e.target.closest("form[action$='/cart/add']");
        if (!form) return;
        e.preventDefault();
        this.add(form);
      });

      // Quick-add buttons (collection cards)
      document.addEventListener("click", (e) => {
        const btn = e.target.closest("[data-quick-add]");
        if (!btn) return;
        e.preventDefault();
        this.addById(btn.dataset.variantId, parseInt(btn.dataset.qty || "1", 10), btn);
      });

      this.refresh();
    },

    async refresh() {
      try {
        const r = await fetch("/cart.js", { headers: { Accept: "application/json" } });
        const cart = await r.json();
        this.render(cart);
      } catch (_) { /* noop */ }
    },

    open() {
      if (!this.el) return;
      this.el.setAttribute("data-open", "true");
      if (this.backdrop) this.backdrop.setAttribute("data-open", "true");
      document.body.style.overflow = "hidden";
      this.refresh();
    },
    close() {
      if (!this.el) return;
      this.el.setAttribute("data-open", "false");
      if (this.backdrop) this.backdrop.setAttribute("data-open", "false");
      document.body.style.overflow = "";
    },

    async add(form) {
      const submitBtn = form.querySelector("[type=submit]");
      if (submitBtn) submitBtn.setAttribute("aria-busy", "true");
      const fd = new FormData(form);
      try {
        const res = await fetch("/cart/add.js", {
          method: "POST",
          body: fd,
          headers: { Accept: "application/json", "X-Requested-With": "XMLHttpRequest" },
        });
        if (!res.ok) {
          const err = await res.json().catch(() => ({}));
          this.flash(err.description || err.message || "No se pudo agregar al carrito.", true);
          return;
        }
        await this.refresh();
        this.open();
      } catch (_) {
        this.flash("Error de red. Intenta de nuevo.", true);
      } finally {
        if (submitBtn) submitBtn.removeAttribute("aria-busy");
      }
    },

    async addById(variantId, qty, btn) {
      if (!variantId) return;
      if (btn) btn.setAttribute("aria-busy", "true");
      try {
        const res = await fetch("/cart/add.js", {
          method: "POST",
          headers: { "Content-Type": "application/json", Accept: "application/json", "X-Requested-With": "XMLHttpRequest" },
          body: JSON.stringify({ items: [{ id: parseInt(variantId, 10), quantity: qty }] }),
        });
        if (!res.ok) {
          const err = await res.json().catch(() => ({}));
          this.flash(err.description || err.message || "No se pudo agregar al carrito.", true);
          return;
        }
        await this.refresh();
        this.open();
      } catch (_) {
        this.flash("Error de red. Intenta de nuevo.", true);
      } finally {
        if (btn) btn.removeAttribute("aria-busy");
      }
    },

    async change(key, quantity) {
      try {
        const res = await fetch("/cart/change.js", {
          method: "POST",
          headers: { "Content-Type": "application/json", Accept: "application/json" },
          body: JSON.stringify({ id: key, quantity }),
        });
        const cart = await res.json();
        this.render(cart);
      } catch (_) { /* noop */ }
    },

    render(cart) {
      const count = cart.item_count || 0;
      this.countEls.forEach((el) => {
        el.textContent = count;
        el.style.display = count > 0 ? "" : "none";
      });

      if (!this.bodyEl) return;

      if (!cart.items || cart.items.length === 0) {
        this.bodyEl.innerHTML = `
          <div class="drawer__empty">
            <div class="drawer__empty-icon">✦</div>
            <p class="title">Tu carrito está vacío</p>
            <p class="sub">Descubre nuestras mieles artesanales.</p>
            <a href="/collections/mieles" class="btn btn--outline" style="margin-top:1.5rem;">Explorar mieles</a>
          </div>`;
        if (this.footEl) this.footEl.style.display = "none";
        if (this.shipEl) this.renderShip(0);
        return;
      }

      const items = cart.items
        .map(
          (i) => `
        <li class="cart-item" data-key="${i.key}">
          <img class="cart-item__img" src="${i.image || i.featured_image?.url || ""}" width="80" height="80" loading="lazy" alt="${escapeHtml(i.product_title)}">
          <div class="cart-item__body">
            <a class="cart-item__title" href="${i.url}">${escapeHtml(i.product_title)}</a>
            ${i.variant_title && i.variant_title !== "Default Title" ? `<div class="cart-item__variant">${escapeHtml(i.variant_title)}</div>` : ""}
            <div class="cart-item__row">
              <div class="qty-stepper" role="group" aria-label="Cantidad">
                <button type="button" aria-label="Restar" data-qty-dec>−</button>
                <span class="v">${i.quantity}</span>
                <button type="button" aria-label="Sumar" data-qty-inc>+</button>
              </div>
              <span class="cart-item__price">${money(i.line_price)}</span>
            </div>
            <button class="cart-item__remove" type="button" data-cart-remove>Eliminar</button>
          </div>
        </li>`
        )
        .join("");

      this.bodyEl.innerHTML = `<ul class="cart-items">${items}</ul>`;

      if (this.footEl) {
        this.footEl.style.display = "";
        const subtotal = $("[data-cart-subtotal]", this.footEl);
        if (subtotal) subtotal.textContent = money(cart.total_price);
      }

      this.renderShip(cart.total_price);
      this.bindItemActions();
    },

    renderShip(total) {
      if (!this.shipEl) return;
      const free = this.free;
      const remaining = Math.max(0, free - total);
      const progress = Math.min(100, (total / free) * 100);
      const msgEl = $("[data-ship-msg]", this.shipEl);
      const barEl = $("[data-ship-bar]", this.shipEl);
      if (msgEl) {
        msgEl.innerHTML =
          remaining > 0
            ? `Te faltan <strong>${money(remaining)}</strong> para envío gratis`
            : `<strong>¡Envío gratis desbloqueado!</strong>`;
      }
      if (barEl) barEl.style.width = progress + "%";
    },

    bindItemActions() {
      $$(".cart-item", this.bodyEl).forEach((li) => {
        const key = li.dataset.key;
        const v = li.querySelector(".qty-stepper .v");
        li.querySelector("[data-qty-dec]")?.addEventListener("click", () => {
          this.change(key, Math.max(0, parseInt(v.textContent, 10) - 1));
        });
        li.querySelector("[data-qty-inc]")?.addEventListener("click", () => {
          this.change(key, parseInt(v.textContent, 10) + 1);
        });
        li.querySelector("[data-cart-remove]")?.addEventListener("click", () => {
          this.change(key, 0);
        });
      });
    },

    flash(msg, isError) {
      const existing = $("[data-cart-flash]");
      if (existing) existing.remove();
      const el = document.createElement("div");
      el.setAttribute("data-cart-flash", "");
      el.style.cssText = "position:fixed;left:50%;top:1.5rem;transform:translateX(-50%);z-index:9999;padding:.75rem 1rem;font-size:.85rem;background:" + (isError ? "#3a1f1f" : "#1f3a26") + ";color:#fff;border:1px solid " + (isError ? "#e07b7b" : "#2e5c40") + ";";
      el.textContent = msg;
      document.body.appendChild(el);
      setTimeout(() => el.remove(), 3000);
    },
  };

  function escapeHtml(s) {
    return String(s).replace(/[&<>"']/g, (c) => ({ "&": "&amp;", "<": "&lt;", ">": "&gt;", '"': "&quot;", "'": "&#39;" }[c]));
  }

  /* ---------- PRODUCT PAGE ---------- */
  function initProduct() {
    const form = $("[data-product-form]");
    if (!form) return;
    const data = JSON.parse($("[data-product-json]")?.textContent || "{}");
    if (!data.variants) return;

    const optionInputs = $$(".option-pill[data-option-index]", form);
    const variantIdInput = $("[name='id']", form);
    const priceEl = $("[data-price]");
    const compareEl = $("[data-compare-price]");
    const subEl = $("[data-product-submit]");
    const stickyPrice = $("[data-sticky-price]");
    const stickyBtn = $("[data-sticky-add]");

    const opts = data.options?.length || 1;
    const selected = new Array(opts).fill(null);

    // Initialize from first available variant
    const firstAvail = data.variants.find((v) => v.available) || data.variants[0];
    if (firstAvail) {
      [firstAvail.option1, firstAvail.option2, firstAvail.option3].forEach((v, i) => { if (v) selected[i] = v; });
    }

    function refresh() {
      // Mark active pills
      optionInputs.forEach((b) => {
        const idx = parseInt(b.dataset.optionIndex, 10);
        b.classList.toggle("is-active", selected[idx] === b.dataset.optionValue);
      });
      const match = data.variants.find((v) =>
        (v.option1 || null) === (selected[0] || null) &&
        (v.option2 || null) === (selected[1] || null) &&
        (v.option3 || null) === (selected[2] || null)
      );
      if (!match) return;
      if (variantIdInput) variantIdInput.value = match.id;
      const url = new URL(window.location.href);
      url.searchParams.set("variant", match.id);
      window.history.replaceState({}, "", url.toString());

      if (priceEl) priceEl.innerHTML = money(match.price) + ' <span class="currency">MXN</span>';
      if (compareEl) {
        if (match.compare_at_price && match.compare_at_price > match.price) {
          compareEl.textContent = money(match.compare_at_price);
          compareEl.style.display = "";
        } else {
          compareEl.style.display = "none";
        }
      }
      if (stickyPrice) stickyPrice.textContent = money(match.price);

      const available = match.available;
      [subEl, stickyBtn].forEach((b) => {
        if (!b) return;
        b.disabled = !available;
        b.setAttribute("aria-disabled", String(!available));
        if (!available) b.textContent = "Agotado";
        else b.textContent = b.dataset.originalLabel || b.textContent;
      });
    }

    optionInputs.forEach((b) => {
      if (subEl) subEl.dataset.originalLabel = subEl.textContent.trim();
      if (stickyBtn) stickyBtn.dataset.originalLabel = stickyBtn.textContent.trim();
      b.addEventListener("click", () => {
        selected[parseInt(b.dataset.optionIndex, 10)] = b.dataset.optionValue;
        refresh();
      });
    });

    // Qty stepper on PDP
    const qtyInput = $("[data-qty-input]", form);
    $("[data-qty-dec]", form)?.addEventListener("click", () => {
      qtyInput.value = Math.max(1, parseInt(qtyInput.value || "1", 10) - 1);
    });
    $("[data-qty-inc]", form)?.addEventListener("click", () => {
      qtyInput.value = parseInt(qtyInput.value || "1", 10) + 1;
    });

    // Sticky add-to-cart: mirror form submit
    stickyBtn?.addEventListener("click", () => form.requestSubmit());

    // Show sticky ATC after scrolling past the panel
    const sticky = $(".sticky-atc");
    const panel = $(".pdp__panel");
    if (sticky && panel) {
      const onScroll = () => {
        const r = panel.getBoundingClientRect();
        sticky.classList.toggle("is-shown", r.bottom < window.innerHeight * 0.3 || r.top > window.innerHeight);
      };
      onScroll();
      window.addEventListener("scroll", onScroll, { passive: true });
    }

    // Thumbnail gallery
    $$(".pdp__thumb").forEach((t) => {
      t.addEventListener("click", () => {
        const src = t.querySelector("img").src;
        const hero = $(".pdp__hero img");
        if (hero) hero.src = src;
        $$(".pdp__thumb").forEach((x) => x.classList.remove("is-active"));
        t.classList.add("is-active");
      });
    });

    refresh();
  }

  /* ---------- COLLECTION FILTERS (in-page, by tag) ---------- */
  function initCollectionFilters() {
    const root = $("[data-collection-filter]");
    if (!root) return;
    const chips = $$(".filter-chip", root);
    const cards = $$("[data-tags]");
    const sort = $("[data-collection-sort]");
    const grid = $("[data-collection-grid]");

    function applyFilter() {
      const active = root.querySelector(".filter-chip.is-active");
      const tag = active?.dataset.filter || "all";
      cards.forEach((c) => {
        const tags = (c.dataset.tags || "").split("|");
        const show = tag === "all" || tags.includes(tag);
        c.style.display = show ? "" : "none";
      });
    }
    function applySort() {
      if (!sort || !grid) return;
      const mode = sort.value;
      const arr = $$("[data-tags]", grid).slice();
      arr.sort((a, b) => {
        const pa = parseFloat(a.dataset.price || "0");
        const pb = parseFloat(b.dataset.price || "0");
        if (mode === "menor") return pa - pb;
        if (mode === "mayor") return pb - pa;
        return 0;
      });
      arr.forEach((el) => grid.appendChild(el));
    }
    chips.forEach((c) => c.addEventListener("click", () => {
      chips.forEach((x) => x.classList.remove("is-active"));
      c.classList.add("is-active");
      applyFilter();
    }));
    sort?.addEventListener("change", applySort);
  }

  /* ---------- NEWSLETTER (server-side form, JS only enhances visuals) ---------- */
  function initNewsletter() {
    // Server-side post handled by Shopify form 'customer'.
    // After redirect, success message is rendered via Liquid with class .form-success.
    // No JS interception — let Shopify handle it natively for max reliability.
  }

  /* ---------- BOOT ---------- */
  function boot() {
    initHeaderScroll();
    initMobileMenu();
    Cart.init();
    initProduct();
    initCollectionFilters();
    initNewsletter();
  }
  if (document.readyState === "loading") document.addEventListener("DOMContentLoaded", boot);
  else boot();

  // Expose for inline buttons if needed
  window.YA_Cart = Cart;
})();
